import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Percent } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { notificar } from '@/shared/ui/avisos';
import {
  CampoDelPanel,
  DosColumnas,
  PanelLateral,
  claseEntradaPanel,
} from '@/shared/ui/PanelLateral';
import { mensajeDe } from './mensajes';
import type { ConceptoDeRetencion, Impuesto } from './contrato';

export function PanelDelConcepto({
  concepto,
  alCerrar,
  alGuardar,
}: {
  concepto: ConceptoDeRetencion | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = concepto !== null;
  const [impuesto, setImpuesto] = useState<Impuesto>(concepto?.impuesto ?? '1');
  const [codigo, setCodigo] = useState(concepto?.codigo ?? '');
  const [descripcion, setDescripcion] = useState(concepto?.descripcion ?? '');
  const [variable, setVariable] = useState(concepto ? !concepto.seAplicaSolo : false);
  const [porcentaje, setPorcentaje] = useState(
    concepto?.porcentaje === undefined ? '' : String(concepto.porcentaje),
  );
  const [nota, setNota] = useState(concepto?.notaPorcentaje ?? '');
  const [desde, setDesde] = useState(concepto?.vigenteDesde ?? '');
  const [error, setError] = useState<string | null>(null);

  const vigenteYConPorcentaje = editando && !concepto.vigenteHasta && concepto.seAplicaSolo;

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        impuesto,
        codigo: codigo.trim(),
        descripcion: descripcion.trim(),
        porcentaje: variable ? null : Number(porcentaje),
        notaPorcentaje: variable ? nota.trim() : null,
        vigenteDesde: desde,
        vigenteHasta: concepto?.vigenteHasta ?? null,
        activo: concepto?.activo ?? true,
      };
      return editando
        ? api.put<ConceptoDeRetencion>(`/retenciones/conceptos/${concepto.id}`, cuerpo)
        : api.post<ConceptoDeRetencion>('/retenciones/conceptos', cuerpo);
    },
    onSuccess: () => {
      notificar.exito(editando ? 'Concepto actualizado' : 'Concepto cargado');
      alGuardar();
    },
    onError: (causa) => {
      const mensaje = mensajeDe(causa, 'No se pudo guardar');
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const falta =
    codigo.trim() === ''
      ? 'Falta el código del anexo'
      : descripcion.trim() === ''
        ? 'Falta la descripción'
        : desde === ''
          ? 'Falta desde cuándo se aplica'
          : variable
            ? nota.trim() === ''
              ? 'Escriba qué dice la norma: «0 al 37», «según la tabla»…'
              : null
            : porcentaje.trim() === ''
              ? 'Falta el porcentaje'
              : null;

  return (
    <PanelLateral
      abierto
      alCerrar={alCerrar}
      icono={Percent}
      titulo={editando ? 'Editar concepto' : 'Nuevo concepto'}
      descripcion="El porcentaje que fija la resolución, con la fecha desde la que rige"
      alEnviar={(evento) => {
        evento.preventDefault();
        if (falta === null) {
          guardar.mutate();
        }
      }}
      enviando={guardar.isPending}
    >
      <div className="space-y-5">
        <DosColumnas>
          <CampoDelPanel etiqueta="Impuesto" requerido>
            <select
              value={impuesto}
              onChange={(e) => setImpuesto(e.target.value as Impuesto)}
              className={claseEntradaPanel}
            >
              <option value="1">IMPUESTO A LA RENTA</option>
              <option value="2">IVA</option>
            </select>
          </CampoDelPanel>

          <CampoDelPanel etiqueta="Código" requerido ayuda="El del anexo: 303, 304A, 1…">
            <input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              maxLength={10}
              className={`${claseEntradaPanel} uppercase`}
            />
          </CampoDelPanel>
        </DosColumnas>

        <CampoDelPanel etiqueta="Descripción" requerido>
          <textarea
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            maxLength={255}
            rows={2}
            className={`${claseEntradaPanel} h-auto py-2`}
          />
        </CampoDelPanel>

        <label className="flex items-center gap-2 text-xs font-semibold uppercase text-armazon-700">
          <input
            type="checkbox"
            checked={variable}
            onChange={(e) => setVariable(e.target.checked)}
            className="size-4 rounded border-armazon-300"
          />
          El porcentaje es variable
        </label>

        {variable ? (
          <CampoDelPanel
            etiqueta="Qué dice la norma"
            requerido
            ayuda="No se puede aplicar solo: lo define quien liquida"
          >
            <input
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              maxLength={160}
              placeholder="0 al 37"
              className={claseEntradaPanel}
            />
          </CampoDelPanel>
        ) : (
          <CampoDelPanel etiqueta="Porcentaje" requerido>
            <input
              type="number"
              step="0.001"
              min="0"
              max="100"
              value={porcentaje}
              onChange={(e) => setPorcentaje(e.target.value)}
              className={claseEntradaPanel}
            />
          </CampoDelPanel>
        )}

        <CampoDelPanel
          etiqueta="Rige desde"
          requerido
          ayuda="La fecha de la resolución que lo fija"
        >
          <input
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            className={claseEntradaPanel}
          />
        </CampoDelPanel>

        {vigenteYConPorcentaje && (
          <p className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-justify text-[11px] uppercase leading-relaxed text-amber-900">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              <span className="font-semibold">Este concepto sigue vigente.</span> Se puede corregir
              su redacción, pero no su porcentaje: eso reescribiría retenciones ya emitidas. Para
              un porcentaje nuevo, cierre este y cree otro desde la fecha de la resolución.
            </span>
          </p>
        )}

        {(error ?? falta) && (
          <p
            role={error ? 'alert' : undefined}
            className={`text-[11px] font-semibold uppercase ${
              error ? 'text-red-700' : 'text-armazon-500'
            }`}
          >
            {error ?? falta}
          </p>
        )}
      </div>
    </PanelLateral>
  );
}
